import { action, computed, makeObservable, observable } from 'mobx';
import { Industry, TeamMemberRoleInput } from '@clipr/lib';

import { CatalogState } from '../../components/layout/catalogState';
import { DeleteTeamWindowState } from '../../components/teams/deleteTeamWindowState';
import { TeamProductWindowState } from '../../components/teams/teamProductWindowState';
import { AsyncResult, Result } from '../../core';
import { Error, pageError } from '../../core/error';
import { Team } from '../../entities';
import { AdminTeamsCatalogSource } from '../../entities/adminTeamsCatalogSource';
import { Message, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { parseTradeShowLeadsCsv, TradeShowLeadCsvRow } from '../agencySignupPage/agencyCsv';

export type AdminTeamsPageParams = {
};

/** Controller for AdminTeamsPage. */
export class AdminTeamsPageState
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'setError' | 'setLoaded'>(this, {
      isLoading: observable,
      error: observable,
      isTradeShowCsvUploading: observable,
      tradeShowInviteMessage: observable,
      tradeShowCsvStatus: observable,
      tradeShowCsvErrors: observable,
      pageMounted: action,
      pageUnmounted: action,
      openTeamProductUpdate: action,
      refreshTeams: action,
      updateTradeShowInviteMessage: action,
      hasTradeShowInviteMessage: computed,
      canUploadTradeShowLeadsCsv: computed,
      importTradeShowLeadsCsv: action,
      reset: action,
      setError: action,
      setLoaded: action,
    });

    this.teamCatalogSource = new AdminTeamsCatalogSource(this.store, {
      pageSize: 20
    });
    this.teamCatalog = new CatalogState(this.store, {
      syncStatus: () => this.teamCatalogSource.syncStatus,
      isEndOfList: () => this.teamCatalogSource.isEndOfList
    });
    this.teamCatalog.listen((msg) => {
      switch (msg.type) {
        case 'scrollToBottom':
        case 'loadMore':
          this.teamCatalogSource.fetchMore();
          break;
      }
    });
  }

  isLoading: boolean = false;
  error: Error | null = null;
  isTradeShowCsvUploading = false;
  tradeShowInviteMessage = '';
  tradeShowCsvStatus: string | null = null;
  tradeShowCsvErrors: string[] = [];

  readonly teamCatalogSource: AdminTeamsCatalogSource;
  readonly teamCatalog = new CatalogState(this.store);

  async pageMounted(_params: AdminTeamsPageParams) {
    const { deleteTeamWindow } = this.store;

    deleteTeamWindow.listen(
      this.deleteTeamWindowListener)

    this.reset();
    this.isLoading = true;

    const requests: AsyncResult[] = [
      this.teamCatalogSource.fetch(),
      this.teamCatalogSource.fetchTeamNamespaces()
    ];

    const [[, err1], [, err2]]: Result[] = await Promise.all(requests);

    if (err1 || err2)
      return this.setError(err1 || err2);

    this.setLoaded();
  }

  private deleteTeamWindowListener = action((msg: Message<DeleteTeamWindowState>) => {
    switch (msg.type) {
      case 'team:deleted':
        this.refreshTeams();
        break;
    }
  });

  pageUnmounted() {
    this.reset();

    this.store.deleteTeamWindow.unlisten(
      this.deleteTeamWindowListener);
  }

  openTeamProductUpdate(team: Team) {
    const window = this.store.teamProductWindow;
    const listener = (msg: Message<TeamProductWindowState>) => {
      switch (msg.type) {
        case 'teamUpdated':
          this.refreshTeams();
          window.unlisten(listener);
          break;
        case 'close':
          window.unlisten(listener);
          break;
      }
    }
    window.listen(listener);
    window.open({ team })
  }

  // #region State helpers
  async refreshTeams() {
    const requests: AsyncResult[] = [
      this.teamCatalogSource.fetch(),
      this.teamCatalogSource.fetchTeamNamespaces()
    ];

    return await Promise.all(requests);
  }

  updateTradeShowInviteMessage(value: string) {
    this.tradeShowInviteMessage = value;
  }

  get hasTradeShowInviteMessage() {
    return this.tradeShowInviteMessage.trim().length > 0;
  }

  get canUploadTradeShowLeadsCsv() {
    return !this.isTradeShowCsvUploading && this.hasTradeShowInviteMessage;
  }

  async importTradeShowLeadsCsv(data: string[][]) {
    this.tradeShowCsvStatus = null;
    this.tradeShowCsvErrors = [];

    if (!this.hasTradeShowInviteMessage) {
      this.tradeShowCsvErrors = ['Invite message is required before uploading leads.'];
      return;
    }

    const parsed = parseTradeShowLeadsCsv(data);
    this.tradeShowCsvErrors = parsed.errors;

    if (parsed.rows.length === 0) {
      if (this.tradeShowCsvErrors.length === 0)
        this.tradeShowCsvErrors = ['No leads were found in the CSV.'];
      return;
    }

    this.isTradeShowCsvUploading = true;
    let successCount = 0;

    for (const lead of parsed.rows) {
      const error = await this.createLibraryForTradeShowLead(lead);
      if (error) {
        this.tradeShowCsvErrors = [...this.tradeShowCsvErrors, `${lead.email}: ${error}`];
      } else {
        successCount += 1;
      }
    }

    this.isTradeShowCsvUploading = false;
    this.tradeShowCsvStatus = `${successCount} lead${successCount === 1 ? '' : 's'} imported.`;

    if (successCount > 0)
      await this.refreshTeams();
  }

  reset() {
    this.isLoading = false;
    this.error = null;
    this.isTradeShowCsvUploading = false;
    this.tradeShowInviteMessage = '';
    this.tradeShowCsvStatus = null;
    this.tradeShowCsvErrors = [];
    this.teamCatalogSource.searchFilter = null;
    this.teamCatalogSource.searchBarModel.clear();
  }

  private setError(error?: Error) {
    if (!error)
      error = pageError();

    this.isLoading = false;
    this.error = error;
  }

  private setLoaded() {
    this.isLoading = false;
    this.error = null;
  }
  // #endregion

  private async createLibraryForTradeShowLead(lead: TradeShowLeadCsvRow): Promise<string | null> {
    const libraryName = `${lead.fullName} ${lead.agency} - ${lead.email}`;
    const description = [lead.rank, lead.fullName, lead.email].filter(Boolean).join(' - ');
    const message = this.tradeShowInviteMessage.trim();
    const [teamRes, teamErr] = await this.store.apiService.createTeam({
      args: {
        name: libraryName,
        displayName: libraryName,
        description,
        externalEmail: lead.email,
        // Leads are police. Industry keeps the library public safety while the report follows
        // each invitee's own default rather than a library-wide speciality.
        industry: Industry.PublicSafety,
      },
    });

    if (teamErr)
      return teamErr.message;

    const teamId = teamRes?.createTeam.id;
    if (!teamId)
      return 'Library was not created.';

    const [, inviteErr] = await this.store.apiService.inviteTeamMember({
      teamId,
      email: lead.email,
      role: TeamMemberRoleInput.Admin,
      message: message || undefined,
    });

    return inviteErr?.message ?? null;
  }
}
